import type { MouseEventHandler, ReactNode } from "react"
import DesktopScale, { MobileScale } from "./scale"
import type { DesktopScaleProps, MobileScaleProps } from "./scale"
import {
  DesktopStatementCard,
  DesktopStatementHeader,
  MobileExpandable,
  MobileStatementCard,
  MobileStatementHeader,
  MobileStatementText
} from "./ui"

type StatementIdentityProps = {
  id?: string
  statementID?: string
}

type DesktopStatementProps = StatementIdentityProps & {
  children: ReactNode
  actions?: ReactNode
  draft?: boolean
  scaleProps?: Omit<DesktopScaleProps, "statementLabel">
  statementLabel: string
}

export const DesktopStatement = ({
  id,
  statementID,
  statementLabel,
  children,
  actions,
  draft = false,
  scaleProps
}: DesktopStatementProps) => (
  <DesktopStatementCard
    id={id}
    data-likert-statement={statementID}
    $draft={draft}
  >
    <DesktopStatementHeader>
      {children}
      {actions}
    </DesktopStatementHeader>
    {scaleProps ? (
      <DesktopScale statementLabel={statementLabel} {...scaleProps} />
    ) : null}
  </DesktopStatementCard>
)

type MobileStatementProps = StatementIdentityProps & {
  children: ReactNode
  leading?: ReactNode
  trailing?: ReactNode
  clickable?: boolean
  expanded?: boolean
  collapsedSummary?: ReactNode
  scaleProps?: Omit<MobileScaleProps, "statementLabel">
  statementLabel: string
  onCardClick?: MouseEventHandler<HTMLDivElement>
  onHeaderClick?: MouseEventHandler<HTMLDivElement>
}

export const MobileStatement = ({
  id,
  statementID,
  statementLabel,
  children,
  leading,
  trailing,
  clickable,
  expanded = false,
  collapsedSummary,
  scaleProps,
  onCardClick,
  onHeaderClick
}: MobileStatementProps) => (
  <MobileStatementCard
    id={id}
    data-likert-statement={statementID}
    $clickable={Boolean(onCardClick)}
    onClick={onCardClick}
  >
    <MobileStatementHeader
      $clickable={clickable}
      onClick={(event) => {
        if (onCardClick) event.stopPropagation()
        onHeaderClick?.(event)
      }}
    >
      {leading}
      <MobileStatementText>{children}</MobileStatementText>
      {trailing}
    </MobileStatementHeader>
    {scaleProps ? (
      <MobileExpandable $expanded={expanded}>
        <div>
          <MobileScale statementLabel={statementLabel} {...scaleProps} />
        </div>
      </MobileExpandable>
    ) : null}
    {collapsedSummary}
  </MobileStatementCard>
)
